// DecisionFeed — 移动端 AI 思考与决策流（暗色版）
//
// 替代旧的 ThinkingStream（纯展示消息列表），提供与 web-alpha AiOperationStream
// 同级别的 SSE 实时推送 + 多智能体决策管线可视化，但针对移动端优化：
//   - 单列竖排时间线
//   - 触控友好（点击展开而非悬停弹窗）
//   - 紧凑布局，优先展示核心决策内容

import { useEffect, useRef, useState } from 'react';
import {
  listCards, listDebateRounds, listAnalystViews, listLLMTraces, getLLMTrace,
  type AlphaCard, type DebateRound, type AnalystView,
  type LLMTraceSummary, type LLMTraceFull,
} from '@/api/alpha';
import { subscribeAlphaStream, type AlphaStreamEvent } from '@/api/alpha-stream';
import { agentLabel, agentEmoji, agentTypeFromStage } from '@/i18n/agents';
import { prettifyStance } from '@/lib/stance';

// ====== 类型定义 ======

interface FeedEvent {
  id: string;
  ts: number;
  tag: string;
  text: string;
  color: string;
  important?: boolean;
  traceStage?: string;
  decisionRound?: DebateRound;
}

// ====== 常量 ======

const SNAPSHOT_LIMIT = 10000;
const MAX_EVENTS = 2000;
const PAGE_SIZE = 30;

type Phase = 'preOpen' | 'trading' | 'lunch' | 'postClose' | 'evening' | 'holiday';

function currentPhase(): Phase {
  const beijing = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Shanghai' }));
  const wd = beijing.getDay();
  if (wd === 0 || wd === 6) return 'holiday';
  const hm = beijing.getHours() * 60 + beijing.getMinutes();
  if (hm >= 8 * 60 + 55 && hm < 9 * 60 + 30) return 'preOpen';
  if ((hm >= 9 * 60 + 30 && hm <= 11 * 60 + 30) || (hm >= 13 * 60 && hm <= 15 * 60)) return 'trading';
  if (hm > 11 * 60 + 30 && hm < 13 * 60) return 'lunch';
  if (hm > 15 * 60 && hm < 18 * 60) return 'postClose';
  return 'evening';
}

const PHASE_EMPTY: Record<Phase, { icon: string; text: string }> = {
  preOpen: { icon: 'ri-sun-line', text: '盘前（08:55–09:30），AI 正在汇总隔夜信息与开盘策略。' },
  trading: { icon: 'ri-loader-2-line animate-spin', text: 'AI 正在持续扫描全市场，等待第一个判断信号…' },
  lunch: { icon: 'ri-restaurant-line', text: '午间休市（11:30–13:00），13:00 自动恢复。' },
  postClose: { icon: 'ri-moon-line', text: '今日已收市（15:00），明日 09:30 恢复。' },
  evening: { icon: 'ri-moon-line', text: '非交易时段，下个交易日 09:30 启动。' },
  holiday: { icon: 'ri-calendar-line', text: '非交易日，下个交易日 09:30 启动。' },
};

const STAGE_LABEL: Record<string, string> = {
  analysts_started: '🔬 4 位专家开始分析',
  analysts_complete: '✅ 4 位专家分析完成',
  bull_bear_started: '⚖️ 看多/看空辩论中',
  bull_bear_complete: '⚖️ 辩论结束',
  debate_started: '🎯 综合裁决中',
  debate_complete: '🎯 综合决策落地',
  debate_failed: '❌ 综合裁决失败',
  risk_triangle_started: '🛡️ 风控三方审议中',
  risk_triangle_complete: '🛡️ 风控审议完成',
  round_aborted: '⚠️ 本轮中断（专家不足）',
  context_loaded: '📡 市场快照已加载',
};

const ACTIVITY_LABEL: Record<string, [string, string]> = {
  bull: ['🐂', '看多研究员'],
  bear: ['🐻', '看空研究员'],
  research_manager: ['🎯', '研究主管'],
  risk_aggressive: ['🔥', '激进派评审'],
  risk_neutral: ['⚖️', '中立派评审'],
  risk_conservative: ['🛡️', '保守派评审'],
  risk_synth: ['🧭', '风控综合'],
};

// ====== 暗色主题常量 ======
const DARK = {
  bg: '#0f172a',
  border: 'rgba(51,65,85,0.6)',
  text: '#cbd5e1',
  textMuted: '#64748b',
  textDim: '#475569',
  tagBg: (color: string) => `${color}20`,
  tagColor: (color: string) => color,
  rowBg: (color: string) => `${color}08`,
};

// ====== 工具函数 ======

function fmtTime(ts: number): string {
  const d = new Date(ts < 1e12 ? ts * 1000 : ts);
  return [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map(v => String(v).padStart(2, '0')).join(':');
}

function dayKey(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('zh-CN', { timeZone: 'Asia/Shanghai' });
}

function dayLabel(ts: number): string {
  const k = dayKey(ts);
  const nowS = Date.now() / 1000;
  if (k === dayKey(nowS)) return '今天';
  if (k === dayKey(nowS - 86400)) return '昨天';
  return new Date(ts * 1000).toLocaleDateString('zh-CN', {
    month: '2-digit', day: '2-digit', timeZone: 'Asia/Shanghai',
  });
}

function dedupe<T extends { id: number }>(items: T[]): T[] {
  const seen = new Set<number>();
  return items.filter(it => {
    if (it.id == null || seen.has(it.id)) return false;
    seen.add(it.id);
    return true;
  });
}

function phaseEmptyText(phase: Phase) {
  return PHASE_EMPTY[phase] || PHASE_EMPTY.evening;
}

// ====== 主组件 ======

interface DecisionFeedProps {
  sessionId: number | null;
  sessionName?: string;
}

export default function DecisionFeed({ sessionId, sessionName }: DecisionFeedProps) {
  const [cards, setCards] = useState<AlphaCard[]>([]);
  const [rounds, setRounds] = useState<DebateRound[]>([]);
  const [views, setViews] = useState<AnalystView[]>([]);
  const [stageEvents, setStageEvents] = useState<StageEventRow[]>([]);
  const [traceList, setTraceList] = useState<LLMTraceSummary[]>([]);
  const [tracesByStage, setTracesByStage] = useState<Record<string, LLMTraceSummary>>({});
  const [traceTotal, setTraceTotal] = useState(0);

  const [streamLive, setStreamLive] = useState(false);
  const [streamErr, setStreamErr] = useState<string | null>(null);

  const [activities, setActivities] = useState<Record<string, { label: string; detail: string; ts: number }>>({});
  const [marketWatch, setMarketWatch] = useState<{ nextRoundInSec: number; receivedAt: number } | null>(null);
  const [, setClockTick] = useState(0);

  const [tokenStreams, setTokenStreams] = useState<Record<string, string>>({});

  const [expandedTraceId, setExpandedTraceId] = useState<number | null>(null);
  const [expandedTrace, setExpandedTrace] = useState<LLMTraceFull | null>(null);
  const [expandLoading, setExpandLoading] = useState(false);

  const [loading, setLoading] = useState(false);
  const loadRef = useRef(false);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const scrollRef = useRef<HTMLDivElement>(null);

  const feedEvents = buildFeedEvents(cards, rounds, views, stageEvents);

  useEffect(() => {
    if (!sessionId) {
      setCards([]); setRounds([]); setViews([]); setStageEvents([]);
      setTraceList([]); setTracesByStage({}); setTraceTotal(0);
      setStreamLive(false); setStreamErr(null);
      setActivities({}); setMarketWatch(null);
      setExpandedTraceId(null); setExpandedTrace(null);
      setVisibleCount(PAGE_SIZE);
      return;
    }

    setVisibleCount(PAGE_SIZE);
    loadRef.current = true;
    setLoading(true);

    Promise.all([
      listCards(sessionId, SNAPSHOT_LIMIT).then(d => setCards(d.cards ?? [])).catch(() => {}),
      listDebateRounds(sessionId).then(d => setRounds(d.rounds ?? [])).catch(() => {}),
      listAnalystViews(sessionId).then(d => setViews(d.analysts ?? [])).catch(() => {}),
      listLLMTraces(sessionId, { limit: SNAPSHOT_LIMIT }).then(d => {
        const rows = d.traces ?? [];
        setTraceList(rows);
        setTracesByStage(indexTraces(rows));
        setTraceTotal(d.total ?? rows.length);
      }).catch(() => {}),
    ]).finally(() => {
      if (loadRef.current) setLoading(false);
    });

    setStreamErr(null);
    const cancel = subscribeAlphaStream(sessionId, {
      onConnected: () => { setStreamLive(true); setStreamErr(null); },
      onEvent: (e) => handleStreamEvent(e),
      onError: (msg) => setStreamErr(msg),
    });

    return () => {
      cancel();
      setStreamLive(false);
    };
  }, [sessionId]);

  const showWatch = !!marketWatch && Object.keys(activities).length === 0;
  useEffect(() => {
    if (!showWatch) return;
    const t = setInterval(() => setClockTick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [showWatch]);

  useEffect(() => {
    if (Object.keys(activities).length === 0) return;
    const t = setInterval(() => {
      const now = Date.now() / 1000;
      setActivities(prev => {
        let changed = false;
        const next: typeof prev = {};
        for (const [k, v] of Object.entries(prev)) {
          if (now - v.ts < 120) next[k] = v;
          else changed = true;
        }
        return changed ? next : prev;
      });
    }, 10000);
    return () => clearInterval(t);
  }, [activities]);

  const handleStreamEvent = (e: AlphaStreamEvent) => {
    setStreamLive(true);
    setStreamErr(null);

    if (e.type === 'card') {
      setCards(prev => dedupe([e.payload, ...prev]).slice(0, SNAPSHOT_LIMIT));
    } else if (e.type === 'round') {
      setRounds(prev => dedupe([e.payload, ...prev]).slice(0, SNAPSHOT_LIMIT));
      setTokenStreams(prev => {
        if (!prev.debate) return prev;
        const { debate: _drop, ...rest } = prev;
        return rest;
      });
      setActivities(prev => {
        const next = { ...prev };
        delete next.bull; delete next.bear;
        delete next.research_manager; delete next.risk_synth;
        return next;
      });
    } else if (e.type === 'token') {
      const delta = (e.payload as { delta?: string })?.delta || '';
      if (delta) {
        setTokenStreams(prev => ({ ...prev, [e.stage]: (prev[e.stage] || '') + delta }));
      }
    } else if (e.type === 'view') {
      setViews(prev => dedupe([e.payload, ...prev]).slice(0, SNAPSHOT_LIMIT * 4));
      const agentType = e.payload.agentType;
      if (agentType) {
        setActivities(prev => {
          if (!prev[agentType]) return prev;
          const { [agentType]: _drop, ...rest } = prev;
          return rest;
        });
      }
    } else if (e.type === 'market_watch') {
      setMarketWatch({
        nextRoundInSec: e.payload?.nextRoundInSec ?? 0,
        receivedAt: Date.now(),
      });
    } else if (e.type === 'activity') {
      const p = e.payload;
      if (!p.agentType) return;
      setMarketWatch(null);
      const now = Date.now() / 1000;
      if (p.kind === 'heartbeat') {
        setActivities(prev => {
          const cur = prev[p.agentType];
          return { ...prev, [p.agentType]: { label: p.label, detail: cur ? cur.detail : (p.detail || ''), ts: now } };
        });
      } else {
        setActivities(prev => ({ ...prev, [p.agentType]: { label: p.label, detail: p.detail || '', ts: now } }));
      }
    } else if (e.type === 'stage') {
      if (e.stage === 'analysts_started') {
        setTokenStreams({});
        setMarketWatch(null);
      } else if (e.stage === 'debate_started') {
        setTokenStreams(prev => ({ ...prev, debate: '' }));
      } else if (e.stage === 'debate_failed' || e.stage === 'round_aborted') {
        setActivities(prev => {
          const next = { ...prev };
          delete next.bull; delete next.bear;
          delete next.research_manager; delete next.risk_synth;
          return next;
        });
        setTokenStreams(prev => {
          if (!prev.debate) return prev;
          const { debate: _drop, ...rest } = prev;
          return rest;
        });
      }
      setStageEvents(prev => [{
        id: `s-${e.stage}-${Date.now()}`,
        ts: Date.now() / 1000,
        stage: e.stage,
      }, ...prev].slice(0, SNAPSHOT_LIMIT));
    } else if (e.type === 'context') {
      const p = e.payload as { indices?: string[]; quotes?: string[]; news?: string[]; holdingCodes?: string[] };
      const now = Date.now() / 1000;
      const baseId = Date.now();
      const rows: StageEventRow[] = [];
      const summary = [
        p.quotes ? `候选 ${p.quotes.length} 只` : null,
        p.news ? `要闻 ${p.news.length} 条` : null,
        p.holdingCodes?.length ? `持仓 ${p.holdingCodes.length} 只` : null,
      ].filter(Boolean).join(' · ') || '准备中';
      rows.push({ id: `ctx-${baseId}`, ts: now, stage: 'context_loaded', detail: summary });
      setStageEvents(prev => [...rows, ...prev].slice(0, SNAPSHOT_LIMIT));
    } else if (e.type === 'bull_bear') {
      const bb = e.payload as { bull?: string; bear?: string };
      const now = Date.now() / 1000;
      const baseId = Date.now();
      const rows: StageEventRow[] = [];
      if (bb.bull) rows.push({ id: `bull-${baseId}`, ts: now, stage: 'bull_case', detail: bb.bull });
      if (bb.bear) rows.push({ id: `bear-${baseId}`, ts: now - 0.001, stage: 'bear_case', detail: bb.bear });
      setStageEvents(prev => [...rows, ...prev].slice(0, SNAPSHOT_LIMIT));
    } else if (e.type === 'risk_stance') {
      const p = e.payload as { stance?: string; content?: string };
      if (p.content) {
        setStageEvents(prev => [{
          id: `rs-${p.stance}-${Date.now()}`,
          ts: Date.now() / 1000,
          stage: `risk_${p.stance}`,
          detail: prettifyStance(p.content),
        }, ...prev].slice(0, SNAPSHOT_LIMIT));
      }
      if (p.stance) {
        setActivities(prev => {
          const key = `risk_${p.stance}`;
          if (!prev[key]) return prev;
          const { [key]: _drop, ...rest } = prev;
          return rest;
        });
      }
    } else if (e.type === 'risk_summary') {
      const p = e.payload as { adjustedConfidence?: number; summary?: string };
      if (p.summary) {
        setStageEvents(prev => [{
          id: `rsum-${Date.now()}`,
          ts: Date.now() / 1000,
          stage: 'risk_summary',
          detail: `置信度调整为 ${p.adjustedConfidence ?? '?'}/5 — ${p.summary}`,
        }, ...prev].slice(0, SNAPSHOT_LIMIT));
      }
    } else if (e.type === 'trace') {
      const t = e.payload as LLMTraceSummary;
      setTracesByStage(prev => {
        const cur = prev[t.stage];
        if (cur && cur.id >= t.id) return prev;
        return { ...prev, [t.stage]: t };
      });
      setTraceList(prev => [t, ...prev.filter(x => x.id !== t.id)].slice(0, SNAPSHOT_LIMIT));
    }
  };

  const expandTrace = async (stage: string) => {
    const meta = tracesByStage[stage];
    if (!meta) return;
    if (expandedTraceId === meta.id) {
      setExpandedTraceId(null);
      setExpandedTrace(null);
      return;
    }
    setExpandedTraceId(meta.id);
    setExpandedTrace(null);
    setExpandLoading(true);
    try {
      const full = await getLLMTrace(meta.id);
      setExpandedTrace(full);
    } catch (err) {
      setExpandedTrace({
        ...meta, channelId: 0,
        systemPrompt: '', userPrompt: '', rawResponse: '',
        cotTrace: `加载失败：${String(err)}`,
      });
    } finally {
      setExpandLoading(false);
    }
  };

  if (!sessionId) return null;

  const phase = currentPhase();
  const emptyCopy = phaseEmptyText(phase);
  const showFeed = feedEvents.length > 0;
  const visibleEvents = feedEvents.slice(0, visibleCount);
  const allShown = visibleCount >= feedEvents.length;
  const onScroll = (ev: React.UIEvent<HTMLDivElement>) => {
    const el = ev.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      setVisibleCount(c => (c < feedEvents.length ? Math.min(c + PAGE_SIZE, feedEvents.length) : c));
    }
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: DARK.bg, border: '1px solid ' + DARK.border }}>
      {/* 头部 */}
      <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid ' + DARK.border }}>
        <i className="ri-radar-line" style={{ color: '#818cf8', fontSize: 14 }} />
        <span style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>AI 思考与决策流</span>
        <div className="flex items-center gap-1 ml-auto">
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${streamErr ? '' : streamLive ? 'animate-pulse' : ''}`}
            style={{ background: streamErr ? '#ef4444' : streamLive ? '#22c55e' : '#475569' }} />
          <span style={{ fontSize: 9, color: DARK.textMuted }}>
            {streamErr ? '异常' : streamLive ? '实时' : '连接中'}
          </span>
        </div>
        {traceTotal > 0 && (
          <span style={{ fontSize: 9, color: DARK.textDim }}>· {traceTotal} 次思考</span>
        )}
      </div>

      {/* 4 专家名称+状态 */}
      <div className="grid grid-cols-4 gap-1 px-3 pt-3">
        {(['fundamental', 'technical', 'sentiment', 'news'] as const).map(type => {
          const busy = !!activities[type];
          const name = agentLabel(type);
          return (
            <div key={type} style={{
              borderRadius: 8, padding: '4px 8px', border: '1px solid',
              fontSize: 10, display: 'flex', alignItems: 'center', gap: 6,
              background: busy ? 'rgba(99,102,241,0.12)' : 'rgba(30,41,59,0.8)',
              borderColor: busy ? 'rgba(99,102,241,0.3)' : 'rgba(51,65,85,0.4)',
              color: busy ? '#a5b4fc' : '#64748b',
            }}>
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${busy ? 'animate-pulse' : ''}`}
                style={{ background: busy ? '#818cf8' : '#475569' }} />
              <span className="truncate">{name}</span>
            </div>
          );
        })}
      </div>

      {/* AI 正在工作条 */}
      {Object.keys(activities).length > 0 && (
        <div className="mx-3 mt-3 rounded-lg px-3 py-2 text-xs"
          style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}>
          <div className="flex items-center gap-1.5 mb-1">
            <i className="ri-pulse-line" style={{ color: '#818cf8', fontSize: 14 }} />
            <span style={{ fontWeight: 600, color: '#a5b4fc' }}>
              AI 正在工作 ({Object.keys(activities).length})
            </span>
          </div>
          <div className="space-y-1">
            {Object.entries(activities).sort(([a], [b]) => a.localeCompare(b)).map(([key, a]) => {
              const override = ACTIVITY_LABEL[key];
              const emoji = override ? override[0] : (agentEmoji(key) || '🤖');
              const name = override ? override[1] : (agentLabel(key) || key);
              return (
                <div key={key} className="flex items-center gap-1" style={{ fontSize: 11, color: '#818cf8' }}>
                  <span className="w-1 h-1 rounded-full shrink-0 animate-pulse" style={{ background: '#818cf8' }} />
                  <span className="truncate">{emoji} {name} — {a.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 盯盘中 */}
      {showWatch && marketWatch && (() => {
        const elapsed = (Date.now() - marketWatch.receivedAt) / 1000;
        const remain = Math.max(0, Math.round(marketWatch.nextRoundInSec - elapsed));
        return (
          <div className="mx-3 mt-3 rounded-lg px-3 py-2 text-xs"
            style={{ background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(51,65,85,0.4)' }}>
            <div className="flex items-center gap-2">
              <i className="ri-radar-line animate-pulse" style={{ color: '#64748b', fontSize: 14 }} />
              <span style={{ fontWeight: 500, color: '#94a3b8' }}>AI 盯盘中</span>
              {remain > 0 ? (
                <span className="ml-auto" style={{ color: DARK.textMuted }}>
                  距下一轮 <span className="tabular-nums" style={{ fontWeight: 600, color: '#cbd5e1' }}>{remain}s</span>
                </span>
              ) : (
                <span className="ml-auto" style={{ color: DARK.textMuted }}>正在开始下一轮…</span>
              )}
            </div>
          </div>
        );
      })()}

      {/* 流式决策进度条 */}
      {tokenStreams.debate && (
        <div className="mx-3 mt-3 rounded-lg px-3 py-2 text-xs"
          style={{ background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.25)' }}>
          <div className="flex items-center gap-1.5 mb-1">
            <i className="ri-flashlight-line" style={{ color: '#f59e0b', fontSize: 14 }} />
            <span style={{ fontWeight: 600, color: '#fbbf24' }}>研究主管正在书写决策</span>
            <span className="ml-auto tabular-nums" style={{ color: '#d97706' }}>
              {tokenStreams.debate.length} 字
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="inline-block w-1 h-3 rounded-sm animate-pulse" style={{ background: '#f59e0b' }} />
          </div>
        </div>
      )}

      {/* 事件流 */}
      <div ref={scrollRef} onScroll={onScroll}
        className="overflow-y-auto px-3 pb-2"
        style={{ maxHeight: '35vh' }}>
        {loading && (
          <div className="flex items-center justify-center py-8" style={{ color: DARK.textMuted }}>
            <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin mr-2"
              style={{ borderColor: '#818cf8', borderTopColor: 'transparent' }} />
            <span className="text-xs">加载中...</span>
          </div>
        )}
        {!showFeed && !loading && (
          <div className="flex flex-col items-center py-8 gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(51,65,85,0.4)' }}>
              <i className={`${emptyCopy.icon}`} style={{ color: '#818cf8', fontSize: 16 }} />
            </div>
            <p className="text-xs" style={{ color: DARK.textMuted }}>{emptyCopy.text}</p>
          </div>
        )}
        {showFeed && (
          <div className="space-y-0.5 mt-2">
            {visibleEvents.map((e, i) => {
              const showDay = i === 0 || dayKey(e.ts) !== dayKey(visibleEvents[i - 1].ts);
              const traceMeta = e.traceStage ? tracesByStage[e.traceStage] : undefined;
              const canExpand = !!traceMeta;
              const isOpen = canExpand && expandedTraceId === traceMeta!.id;

              return (
                <div key={e.id}>
                  {showDay && (
                    <div className="flex items-center gap-2 py-1.5">
                      <div style={{ height: 1, flex: 1, background: DARK.border }} />
                      <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ color: DARK.textDim, background: DARK.bg }}>
                        {dayLabel(e.ts)}
                      </span>
                      <div style={{ height: 1, flex: 1, background: DARK.border }} />
                    </div>
                  )}
                  <div className="flex items-start gap-1.5 py-1 text-xs"
                    style={e.important ? {
                      borderLeft: '2px solid ' + e.color,
                      paddingLeft: 6, background: DARK.rowBg(e.color), borderRadius: 4,
                    } : undefined}>
                    <div className="flex flex-col items-center shrink-0 mt-0" style={{ width: 52 }}>
                      <span className="px-1 py-px rounded text-[9px] font-medium leading-tight text-center truncate max-w-full"
                        style={{ background: DARK.tagBg(e.color), color: DARK.tagColor(e.color) }}>{e.tag}</span>
                      <span className="text-[9px] font-mono tabular-nums leading-tight mt-px" style={{ color: DARK.textDim }}>{fmtTime(e.ts)}</span>
                    </div>
                    <span className="flex-1 min-w-0 leading-relaxed" style={{ color: '#cbd5e1', fontWeight: e.important ? 500 : 400 }}>
                      {e.text}
                    </span>
                    {canExpand && (
                      <button
                        onClick={() => expandTrace(e.traceStage!)}
                        className="shrink-0 px-1.5 py-0.5 rounded text-[9px] cursor-pointer whitespace-nowrap"
                        style={{
                          background: isOpen ? '#6366f1' : 'rgba(99,102,241,0.15)',
                          color: isOpen ? '#fff' : '#818cf8',
                          border: '1px solid rgba(99,102,241,0.25)',
                        }}>
                        {isOpen ? '收起' : '思考链'}
                      </button>
                    )}
                  </div>

                  {e.decisionRound && <DecisionDetail round={e.decisionRound} />}

                  {isOpen && traceMeta && (
                    <TraceDetail
                      loading={expandLoading}
                      trace={expandedTrace}
                      fallbackMeta={traceMeta}
                    />
                  )}
                </div>
              );
            })}
            <div className="text-center py-2" style={{ fontSize: 9, color: DARK.textDim }}>
              {allShown
                ? `— 已显示全部 ${feedEvents.length} 条 —`
                : `下滑加载更多 · ${visibleEvents.length} / ${feedEvents.length} 条`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ====== 内部类型 ======

interface StageEventRow {
  id: string;
  ts: number;
  stage: string;
  detail?: string;
}

// ====== 内部函数 ======

function indexTraces(rows: LLMTraceSummary[]): Record<string, LLMTraceSummary> {
  const map: Record<string, LLMTraceSummary> = {};
  for (const t of rows) {
    const cur = map[t.stage];
    if (!cur || t.id > cur.id) map[t.stage] = t;
  }
  return map;
}

function buildFeedEvents(
  cards: AlphaCard[],
  rounds: DebateRound[],
  views: AnalystView[],
  stageEvents: StageEventRow[],
): FeedEvent[] {
  const events: FeedEvent[] = [];

  for (const c of cards) {
    events.push({
      id: `c${c.id}`,
      ts: c.createdAt,
      tag: '决策',
      text: `${c.target}(${c.targetCode}) ${c.action}` +
        (c.position ? ` 仓位${c.position}` : '') +
        ` · 置信${c.confidence}/5 — ${c.reason}`,
      color: ['进入', '加仓'].includes(c.action) ? '#ef4444'
        : ['退出', '减仓', '止盈', '止损'].includes(c.action) ? '#22c55e'
        : '#a855f7',
      important: true,
    });
  }

  for (const r of rounds) {
    if (r.bullCase) {
      events.push({
        id: `r${r.id}-bull`, ts: r.createdAt - 10,
        tag: '🐂 看多', color: '#ef4444',
        text: r.bullCase,
      });
    }
    if (r.bearCase) {
      events.push({
        id: `r${r.id}-bear`, ts: r.createdAt - 9,
        tag: '🐻 看空', color: '#22c55e',
        text: r.bearCase,
      });
    }
    if (r.riskStances) {
      try {
        const stances = JSON.parse(r.riskStances) as Record<string, string>;
        const meta: Record<string, [string, string, number]> = {
          aggressive: ['🔥 激进派', '#ef4444', -7],
          neutral: ['⚖️ 中立派', '#3b82f6', -6],
          conservative: ['🛡️ 保守派', '#14b8a6', -5],
        };
        for (const [k, content] of Object.entries(stances)) {
          const m = meta[k];
          if (!m || !content) continue;
          events.push({
            id: `r${r.id}-risk-${k}`, ts: r.createdAt + m[2],
            tag: m[0], color: m[1], text: prettifyStance(content),
          });
        }
      } catch { /* ignore */ }
    }
    if (r.riskAssessment) {
      events.push({
        id: `r${r.id}-risk-sum`, ts: r.createdAt - 1,
        tag: '🛡️ 风控', color: '#a855f7',
        text: r.riskAssessment,
      });
    }
    const execBadge = (() => {
      if (!r.ticker || !r.finalAction) return '';
      if (['观察', '持有', '观望'].includes(r.finalAction)) return ' · 无需下单';
      return r.executed ? ' · ✓已成交' : ' · ⚪未成交';
    })();
    const head = r.ticker
      ? `${r.ticker}(${r.tickerCode}) → ${r.finalAction} 置信${r.confidence}/5` +
        (r.suggestedPosition ? ` 仓位${r.suggestedPosition}%` : '') + execBadge
      : '辩论中（暂无明确标的）';
    events.push({
      id: `r${r.id}`,
      ts: r.createdAt,
      tag: '裁决',
      text: head + (r.reasoning ? `\n${r.reasoning}` : '') +
        [r.expectedPrice, r.stopLoss, r.takeProfit].filter(Boolean).join(' / '),
      color: '#06b6d4',
      important: true,
      decisionRound: r,
    });
  }

  for (const v of views) {
    const persona = agentLabel(v.agentType);
    const body = [v.verdict || '分析中', v.currentMessage || ''].filter(Boolean).join(' — ');
    events.push({
      id: `v${v.id}`,
      ts: v.createdAt,
      tag: persona,
      text: `${agentEmoji(v.agentType)} ${body}`,
      color: '#f59e0b',
    });
  }

  for (const s of stageEvents) {
    events.push({
      id: s.id,
      ts: s.ts,
      tag: '阶段',
      text: (STAGE_LABEL[s.stage] || s.stage) + (s.detail ? ` · ${s.detail}` : ''),
      color: '#6366f1',
    });
  }

  events.sort((a, b) => b.ts - a.ts);
  return events.slice(0, MAX_EVENTS);
}

// ====== 子组件 ======

function DecisionDetail({ round }: { round: DebateRound }) {
  const [open, setOpen] = useState(false);

  function asPoints(v: unknown): { emoji?: string; text?: string }[] {
    if (Array.isArray(v)) return v as { emoji?: string; text?: string }[];
    if (typeof v === 'string' && v.trim()) {
      try { return JSON.parse(v); } catch { /* ignore */ }
    }
    return [];
  }

  const bull = asPoints(round.bullPoints);
  const bear = asPoints(round.bearPoints);
  const targets = [
    round.expectedPrice ? `预期 ${round.expectedPrice}` : null,
    round.stopLoss ? `止损 ${round.stopLoss}` : null,
    round.takeProfit ? `止盈 ${round.takeProfit}` : null,
  ].filter(Boolean).join(' / ');

  return (
    <div className="ml-[104px] mb-1 rounded-lg overflow-hidden"
      style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
      <div className="flex items-center gap-1 px-2.5 py-1.5 cursor-pointer select-none"
        onClick={() => setOpen(o => !o)}>
        <i className={`ri-arrow-${open ? 'down' : 'right'}-s-line`} style={{ color: '#34d399', fontSize: 11 }} />
        <span style={{ fontSize: 10, fontWeight: 600, color: '#6ee7b7' }}>📋 决策详情</span>
        <span className="ml-auto tabular-nums" style={{ fontSize: 9, color: '#34d399' }}>
          {round.finalAction || '-'} · 置信 {round.confidence}/5
        </span>
      </div>
      {open && (
        <div className="px-2.5 pb-2" style={{ fontSize: 10 }}>
          <DecRow k="标的" v={`${round.ticker || '-'}（${round.tickerCode || '-'}）`} />
          <DecRow k="操作" v={round.finalAction || '-'} />
          {round.suggestedPosition > 0 && <DecRow k="建议仓位" v={`${round.suggestedPosition}%`} />}
          {targets && <DecRow k="目标价位" v={targets} />}
          <DecRow k="置信度" v={`${round.confidence}/5（看多 ${round.bullConfidence} / 看空 ${round.bearConfidence}）`} />
          {bull.length > 0 && (
            <DecRow k="🐂 看多" v={bull.map(p => `${p.emoji || ''} ${p.text || ''}`.trim()).filter(Boolean).join('\n')} />
          )}
          {bear.length > 0 && (
            <DecRow k="🐻 看空" v={bear.map(p => `${p.emoji || ''} ${p.text || ''}`.trim()).filter(Boolean).join('\n')} />
          )}
          {round.reasoning && <DecRow k="推理" v={round.reasoning} />}
          {round.riskAssessment && <DecRow k="风险" v={round.riskAssessment} />}
        </div>
      )}
    </div>
  );
}

function DecRow({ k, v }: { k: string; v: string }) {
  if (!v) return null;
  return (
    <div className="flex gap-2" style={{ fontSize: 10 }}>
      <span className="font-medium shrink-0" style={{ color: '#34d399', minWidth: 44 }}>{k}</span>
      <span className="whitespace-pre-line" style={{ color: '#94a3b8' }}>{v}</span>
    </div>
  );
}

function TraceDetail({
  loading, trace, fallbackMeta,
}: {
  loading: boolean;
  trace: LLMTraceFull | null;
  fallbackMeta: LLMTraceSummary;
}) {
  if (loading) {
    return (
      <div className="ml-[104px] mb-1 px-3 py-2 rounded-lg text-[10px]"
        style={{ background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)', color: '#a855f7' }}>
        <i className="ri-loader-2-line animate-spin mr-1" /> 加载思考链…
      </div>
    );
  }

  const t = trace;
  if (!t) return null;

  const stagePretty = (() => {
    const persona = agentTypeFromStage(t.stage);
    if (persona) return agentLabel(persona);
    if (t.stage === 'bull') return '看多研究员';
    if (t.stage === 'bear') return '看空研究员';
    if (t.stage === 'debate') return '研究主管（综合裁决）';
    if (t.stage.startsWith('risk.')) return '风控评审';
    if (t.stage === 'card') return '口播卡片';
    return t.stage;
  })();

  return (
    <div className="ml-[104px] mb-2 rounded-lg overflow-hidden"
      style={{ background: 'rgba(168,85,247,0.06)', border: '1px solid rgba(168,85,247,0.2)' }}>
      <div className="px-2.5 py-1.5 flex items-center gap-1.5 text-[10px]"
        style={{ borderBottom: '1px solid rgba(168,85,247,0.15)' }}>
        <i className="ri-brain-line" style={{ color: '#a855f7' }} />
        <span style={{ fontWeight: 600, color: '#c084fc' }}>{stagePretty}</span>
        <span style={{ color: DARK.textMuted }}>· {t.model || '?'}</span>
        <span style={{ color: DARK.textMuted }}>· {t.durationMs}ms</span>
        {!t.success && (
          <span className="ml-auto px-1 py-px rounded" style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444' }}>失败</span>
        )}
      </div>
      <div className="px-2.5 py-1.5 space-y-1.5">
        <TraceSection title="🧠 思考链 (CoT)" content={t.cotTrace} />
        <TraceSection title="⚙️ System Prompt" content={t.systemPrompt} collapsed />
        <TraceSection title="📥 User Prompt" content={t.userPrompt} collapsed />
        <TraceSection title="📤 Raw Response" content={t.rawResponse} collapsed />
      </div>
    </div>
  );
}

function TraceSection({ title, content, collapsed }: {
  title: string; content: string; collapsed?: boolean;
}) {
  const [open, setOpen] = useState(!collapsed);
  const has = !!content;
  return (
    <div className="rounded" style={{ background: 'rgba(51,65,85,0.3)', border: '1px solid rgba(168,85,247,0.12)' }}>
      <div className="flex items-center gap-1 px-2 py-1 cursor-pointer select-none"
        onClick={() => setOpen(o => !o)}>
        <i className={`ri-arrow-${open ? 'down' : 'right'}-s-line`} style={{ color: '#a855f7', fontSize: 11 }} />
        <span style={{ fontSize: 10, fontWeight: 500, color: '#c084fc' }}>{title}</span>
        {has && <span className="ml-auto" style={{ fontSize: 9, color: DARK.textMuted }}>{content.length} 字</span>}
      </div>
      {open && has && (
        <div className="px-2 pb-1.5 text-[10px] font-mono whitespace-pre-wrap break-words leading-relaxed max-h-[200px] overflow-y-auto"
          style={{ color: '#94a3b8' }}>{content}</div>
      )}
    </div>
  );
}
